const MATCH_MODES = {
  ALL: 'all',
  ANY: 'any',
};

function uniqueSorted(values = []) {
  return [...new Set(values.filter(Boolean))].sort((a, b) => a.localeCompare(b));
}

function listFromValue(value) {
  const values = Array.isArray(value) ? value : [value];

  return values
    .filter((item) => typeof item === 'string')
    .flatMap((item) => item.split(','))
    .map((item) => item.trim())
    .filter(Boolean);
}

function normalizeFilterState(filters = {}, validKindSlugs = [], validTagSlugs = []) {
  const allowedKinds = validKindSlugs.length ? new Set(validKindSlugs) : null;
  const allowedTags = validTagSlugs.length ? new Set(validTagSlugs) : null;
  const matchMode = filters.matchMode === MATCH_MODES.ANY
    ? MATCH_MODES.ANY
    : MATCH_MODES.ALL;

  return {
    kindSlugs: uniqueSorted(listFromValue(filters.kindSlugs)
      .filter((slug) => !allowedKinds || allowedKinds.has(slug))),
    tagSlugs: uniqueSorted(listFromValue(filters.tagSlugs)
      .filter((slug) => !allowedTags || allowedTags.has(slug))),
    matchMode,
  };
}

function filtersEqual(left = {}, right = {}) {
  const normalizedLeft = normalizeFilterState(left);
  const normalizedRight = normalizeFilterState(right);

  return JSON.stringify(normalizedLeft) === JSON.stringify(normalizedRight);
}

function entryMatchesKinds(entry, kindSlugs) {
  return !kindSlugs.length || kindSlugs.includes(entry.kindSlug);
}

function matchingTagSlugs(entry, tagSlugs) {
  const entryTags = new Set(entry.tagSlugs || []);
  return tagSlugs.filter((slug) => entryTags.has(slug));
}

function entryMatchesTags(entry, tagSlugs, matchMode) {
  if (!tagSlugs.length) return true;

  const matched = matchingTagSlugs(entry, tagSlugs);
  return matchMode === MATCH_MODES.ANY
    ? matched.length > 0
    : matched.length === tagSlugs.length;
}

function compareEntries(left, right, filters) {
  if (filters.matchMode === MATCH_MODES.ANY && filters.tagSlugs.length) {
    const matchDifference = right.matchCount - left.matchCount;
    if (matchDifference) return matchDifference;
  }

  const leftDate = left.sortDate || '';
  const rightDate = right.sortDate || '';
  const dateDifference = rightDate.localeCompare(leftDate);
  if (dateDifference) return dateDifference;

  return left.title.localeCompare(right.title);
}

function filterEntries(entries = [], filters = {}) {
  const validKindSlugs = uniqueSorted(entries.map((entry) => entry.kindSlug));
  const validTagSlugs = uniqueSorted(entries.flatMap((entry) => entry.tagSlugs || []));
  const normalized = normalizeFilterState(filters, validKindSlugs, validTagSlugs);

  return entries
    .filter((entry) => entryMatchesKinds(entry, normalized.kindSlugs))
    .map((entry) => {
      const matchedTagSlugs = matchingTagSlugs(entry, normalized.tagSlugs);
      return {
        ...entry,
        matchedTagSlugs,
        matchCount: matchedTagSlugs.length,
      };
    })
    .filter((entry) => entryMatchesTags(entry, normalized.tagSlugs, normalized.matchMode))
    .sort((left, right) => compareEntries(left, right, normalized));
}

function getFacetState(entries = [], tagDefinitions = [], filters = {}) {
  const validKindSlugs = uniqueSorted(entries.map((entry) => entry.kindSlug));
  const validTagSlugs = uniqueSorted(tagDefinitions.map((tag) => tag.slug));
  const normalized = normalizeFilterState(filters, validKindSlugs, validTagSlugs);
  const currentResults = filterEntries(entries, normalized);

  const kindLabels = entries.reduce((labels, entry) => {
    if (labels[entry.kindSlug]) return labels;
    return { ...labels, [entry.kindSlug]: entry.kind };
  }, {});

  const storyTypes = validKindSlugs
    .map((slug) => {
      const count = filterEntries(entries, {
        ...normalized,
        kindSlugs: [slug],
      }).length;

      return {
        slug,
        label: kindLabels[slug],
        count,
        selected: normalized.kindSlugs.includes(slug),
        compatible: count > 0 || normalized.kindSlugs.includes(slug),
      };
    })
    .sort((left, right) => left.label.localeCompare(right.label));

  const tags = tagDefinitions
    .map((tag) => {
      const selected = normalized.tagSlugs.includes(tag.slug);
      const candidateTagSlugs = selected
        ? normalized.tagSlugs
        : [...normalized.tagSlugs, tag.slug];
      const count = filterEntries(entries, {
        ...normalized,
        tagSlugs: candidateTagSlugs,
      }).length;

      return {
        ...tag,
        count,
        selected,
        compatible: selected || count > 0,
      };
    })
    .sort((left, right) => left.label.localeCompare(right.label));

  return {
    filters: normalized,
    results: currentResults,
    resultCount: currentResults.length,
    storyTypes,
    tags,
  };
}

function filtersFromQuery(query = {}, validKindSlugs = [], validTagSlugs = []) {
  return normalizeFilterState({
    kindSlugs: query.types,
    tagSlugs: query.tags,
    matchMode: query.match,
  }, validKindSlugs, validTagSlugs);
}

function queryFromFilters(filters = {}, baseQuery = {}) {
  const normalized = normalizeFilterState(filters);
  const query = { ...baseQuery };

  delete query.types;
  delete query.tags;
  delete query.match;

  if (normalized.kindSlugs.length) query.types = normalized.kindSlugs.join(',');
  if (normalized.tagSlugs.length) query.tags = normalized.tagSlugs.join(',');
  if (normalized.matchMode === MATCH_MODES.ANY) query.match = MATCH_MODES.ANY;

  return query;
}

module.exports = {
  MATCH_MODES,
  filterEntries,
  filtersEqual,
  filtersFromQuery,
  getFacetState,
  normalizeFilterState,
  queryFromFilters,
};
